import { Button } from '../../components/ui/button';
import { FileText, Award, ClipboardCheck, Check, ChevronDown, ChevronUp, AlertTriangle, User, Wind, Users, Clock, X, Settings, LogOut, Shield } from 'lucide-react';
import icons from '../../assets/icons.png';
import logoSygnet from '../../assets/V1_sygnet.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useContext, useEffect, useRef } from 'react';
import { UserContext } from '../../contexts/UserContext';
import ECCNominationTab from './ECCNominationTab';
import ECCWorkPermitTab from './ECCWorkPermitTab';
import ECCWarningActionsTab from './ECCWarningActionsTab';
import WorkScopeRequirementsPage from './ECCNominationsAuthorisation';

const ECCDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(UserContext);

  // Stany dla sidebara
  const [isNominationsOpen, setIsNominationsOpen] = useState(false);
  const [isWorkPermitsOpen, setIsWorkPermitsOpen] = useState(true);
  const [isWarningsOpen, setIsWarningsOpen] = useState(false);
  const [isAuthorizationOpen, setIsAuthorizationOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoPhase, setLogoPhase] = useState(0); // 0: Ventro, 1: Rebranding, 2: Entrigy
  const userMenuRef = useRef(null);

  // Logo animation sequence
  useEffect(() => {
    const timers = [];
    // Phase 0: Ventro (initial) - show for 2 seconds
    timers.push(setTimeout(() => {
      setLogoPhase(1); // Switch to Rebranding
    }, 2000));
    // Phase 1: Rebranding - show for 2 seconds
    timers.push(setTimeout(() => {
      setLogoPhase(2); // Switch to Entrigy
    }, 4000));
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Handle clicks outside user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Stany dla workScopes
  const [workScopes, setWorkScopes] = useState([]);
  const [isWorkScopesLoading, setIsWorkScopesLoading] = useState(true);
  const [workScopesError, setWorkScopesError] = useState(null);
  const [pendingWorkScopeId, setPendingWorkScopeId] = useState(null);

  // Pobieranie listy workScopes dla sidebara
  const fetchWorkScopes = async () => {
    try {
      setIsWorkScopesLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No authentication token found');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workscopes/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch work scopes');
      const data = await response.json();
      setWorkScopes(data);
      setWorkScopesError(null);
    } catch (error) {
      console.error('Error fetching work scopes:', error);
      setWorkScopesError(error.message);
    } finally {
      setIsWorkScopesLoading(false);
    }
  };

  // Funkcja odświeżania workScopes
  const refreshWorkScopes = () => {
    fetchWorkScopes();
  };

  useEffect(() => {
    fetchWorkScopes();
  }, []);

  // Synchronizacja aktywnej zakładki z URL
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const newActiveTab = getActiveTab();

    console.log('useEffect: path=', path, 'newActiveTab=', newActiveTab, 'pendingWorkScopeId=', pendingWorkScopeId);

    if (!isWorkScopesLoading) {
      if (path.startsWith('/ecc/authorization')) {
        const currentWorkScopeId = path.split('/').pop();
        const workScopeExists = workScopes.some((scope) => scope.id === parseInt(currentWorkScopeId));

        if (pendingWorkScopeId) {
          if (workScopeExists && parseInt(currentWorkScopeId) === pendingWorkScopeId) {
            handleTabChange(`authorization-${pendingWorkScopeId}`);
            setPendingWorkScopeId(null);
          } else {
            handleTabChange(`authorization-${pendingWorkScopeId}`);
          }
        } else if (workScopes.length === 0 && path !== '/ecc/authorization/levels') {
          handleTabChange('authorization-levels');
        } else if (path.startsWith('/ecc/authorization/') && !workScopeExists && currentWorkScopeId !== 'levels') {
          handleTabChange('authorization-levels');
        } else if (workScopeExists && activeTab !== `authorization-${currentWorkScopeId}` && !pendingWorkScopeId) {
          handleTabChange(`authorization-${currentWorkScopeId}`);
        }
      } else {
        if (activeTab !== newActiveTab) {
          handleTabChange(newActiveTab);
        }
      }
    }
  }, [isWorkScopesLoading, workScopes, location.pathname, pendingWorkScopeId]);

  // Określanie aktywnej zakładki na podstawie ścieżki
  const getActiveTab = () => {
    const path = location.pathname.toLowerCase();
    console.log('getActiveTab: path=', path);

    if (path === '/ecc' || path === '/ecc/workpermits/pending') return 'workpermits-pending';
    if (path === '/ecc/nominations/pending') return 'nominations-pending';
    if (path === '/ecc/nominations/approved') return 'nominations-approved';
    if (path === '/ecc/nominations/rejected') return 'nominations-rejected';
    if (path === '/ecc/nominations/expired') return 'nominations-expired';
    if (path === '/ecc/nominations/nominees') return 'nominations-nominees';
    if (path === '/ecc/workpermits/approved') return 'workpermits-approved';
    if (path === '/ecc/workpermits/rejected') return 'workpermits-rejected';
    if (path === '/ecc/workpermits/expired') return 'workpermits-expired';
    if (path === '/ecc/workpermits/closed') return 'workpermits-closed';
    if (path === '/ecc/warnings/active') return 'warnings-active';
    if (path === '/ecc/warnings/expired') return 'warnings-expired';
    if (path === '/ecc/authorization/levels') return 'authorization-levels';
    if (path.startsWith('/ecc/authorization/')) {
      const workScopeId = path.split('/').pop();
      const workScopeExists = workScopes.some((scope) => scope.id === parseInt(workScopeId));
      return workScopeExists || parseInt(workScopeId) === pendingWorkScopeId ? `authorization-${workScopeId}` : 'authorization-levels';
    }
    return 'workpermits-pending';
  };

  const activeTab = getActiveTab();

  // Funkcja zmiany aktywnej zakładki
  const handleTabChange = (newValue) => {
    if (newValue === activeTab) return;

    console.log('handleTabChange: newValue=', newValue);

    if (newValue === 'nominations-pending') {
      navigate('/ecc/nominations/pending');
    } else if (newValue === 'nominations-approved') {
      navigate('/ecc/nominations/approved');
    } else if (newValue === 'nominations-rejected') {
      navigate('/ecc/nominations/rejected');
    } else if (newValue === 'nominations-expired') {
      navigate('/ecc/nominations/expired');
    } else if (newValue === 'nominations-nominees') {
      navigate('/ecc/nominations/nominees');
    } else if (newValue === 'workpermits-pending') {
      navigate('/ecc/workpermits/pending');
    } else if (newValue === 'workpermits-approved') {
      navigate('/ecc/workpermits/approved');
    } else if (newValue === 'workpermits-rejected') {
      navigate('/ecc/workpermits/rejected');
    } else if (newValue === 'workpermits-expired') {
      navigate('/ecc/workpermits/expired');
    } else if (newValue === 'workpermits-closed') {
      navigate('/ecc/workpermits/closed');
    } else if (newValue === 'warnings-active') {
      navigate('/ecc/warnings/active');
    } else if (newValue === 'warnings-expired') {
      navigate('/ecc/warnings/expired');
    } else if (newValue === 'authorization-levels') {
      navigate('/ecc/authorization/levels');
    } else if (newValue.startsWith('authorization-')) {
      const workScopeId = newValue.split('-')[1];
      const workScopeExists = workScopes.some((scope) => scope.id === parseInt(workScopeId));
      if (workScopeExists || parseInt(workScopeId) === pendingWorkScopeId) {
        navigate(`/ecc/authorization/${workScopeId}`);
      } else {
        navigate('/ecc/authorization/levels');
      }
    }
  };

  // Funkcja do zmiany aktywnej zakładki dla WorkScopeRequirementsPage
  const onActiveTabChange = (newTab) => {
    console.log('onActiveTabChange: newTab=', newTab);
    if (newTab.startsWith('authorization-')) {
      const workScopeId = parseInt(newTab.split('-')[1]);
      setPendingWorkScopeId(workScopeId);
    }
    handleTabChange(newTab);
    setIsAuthorizationOpen(true);
  };

  // Przekierowanie z /ecc do /ecc/workpermits/pending
  useEffect(() => {
    if (location.pathname.toLowerCase() === '/ecc') {
      console.log('Redirecting from /ecc to /ecc/workpermits/pending');
      navigate('/ecc/workpermits/pending', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleCategoryClick = (category) => {
    console.log('handleCategoryClick: category=', category);
    if (category === 'workpermits') {
      setIsWorkPermitsOpen(true);
      setIsNominationsOpen(false);
      setIsWarningsOpen(false);
      setIsAuthorizationOpen(false);
      handleTabChange('workpermits-pending');
    } else if (category === 'nominations') {
      setIsNominationsOpen(true);
      setIsWorkPermitsOpen(false);
      setIsWarningsOpen(false);
      setIsAuthorizationOpen(false);
      handleTabChange('nominations-pending');
    } else if (category === 'warnings') {
      setIsWarningsOpen(true);
      setIsNominationsOpen(false);
      setIsWorkPermitsOpen(false);
      setIsAuthorizationOpen(false);
      handleTabChange('warnings-active');
    } else if (category === 'authorization') {
      setIsAuthorizationOpen(true);
      setIsNominationsOpen(false);
      setIsWorkPermitsOpen(false);
      setIsWarningsOpen(false);
      if (!isWorkScopesLoading) {
        if (workScopes.length > 0 && !pendingWorkScopeId) {
          handleTabChange(`authorization-${workScopes[0].id}`);
        } else {
          handleTabChange('authorization-levels');
        }
      }
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
    setShowUserMenu(false);
  };

  const confirmLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex flex-col h-screen overflow-y-hidden">
      <div className="flex flex-col md:flex-row h-screen bg-slate-50">
        {/* Sidebar */}
        <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col h-screen sticky top-0">
          {/* Logo/Brand Header with Animation */}
          <div className="h-14 flex items-center justify-center px-3 border-b border-slate-100 overflow-hidden">
            <div className="flex items-center gap-2 relative">
              {/* Ventro Logo - Phase 0 */}
              <div
                className={`flex items-center gap-2 transition-all duration-700 ease-in-out ${
                  logoPhase === 0
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 -translate-x-full absolute'
                }`}
              >
                <img src={logoSygnet} alt="Ventro" className="w-8 h-8 object-contain flex-shrink-0" />
                <span className="font-black text-slate-900 text-sm whitespace-nowrap">Ventro</span>
              </div>

              {/* Rebranding Text - Phase 1 */}
              <div
                className={`flex items-center gap-1 transition-all duration-700 ease-in-out ${
                  logoPhase === 1
                    ? 'opacity-100 translate-x-0'
                    : logoPhase === 0
                      ? 'opacity-0 translate-x-full absolute'
                      : 'opacity-0 -translate-x-full absolute'
                }`}
              >
                <span className="font-bold text-slate-400 text-[10px] whitespace-nowrap">Rebranding</span>
                <span className="text-slate-400 text-xs">→</span>
                <img src={logoSygnet} alt="Entrigy" className="w-5 h-5 object-contain flex-shrink-0" />
                <span className="font-black text-brand-500 text-xs whitespace-nowrap">ENTRIGY</span>
              </div>

              {/* Entrigy - Phase 2 (Final) */}
              <div
                className={`flex items-center gap-2 transition-all duration-700 ease-in-out ${
                  logoPhase === 2
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-full absolute'
                }`}
              >
                <img src={logoSygnet} alt="Entrigy" className="w-8 h-8 object-contain flex-shrink-0" />
                <span className="text-brand-500 text-lg tracking-widest whitespace-nowrap" style={{ fontFamily: 'Arial Black, sans-serif', fontWeight: 900 }}>ENTRIGY</span>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
            <div className="space-y-1">
              {/* Work Permits */}
              <button
                onClick={() => handleCategoryClick('workpermits')}
                className={`flex items-center justify-between w-full px-2 py-2 text-sm font-bold rounded-xl transition-all ${
                  activeTab.startsWith('workpermits') ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <ClipboardCheck size={18} className="flex-shrink-0" />
                  <span>Work Permits</span>
                </div>
                {isWorkPermitsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {isWorkPermitsOpen && (
                <div className="pl-6 py-1 space-y-0.5">
                  <button
                    onClick={() => handleTabChange('workpermits-pending')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'workpermits-pending' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => handleTabChange('workpermits-approved')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'workpermits-approved' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => handleTabChange('workpermits-rejected')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'workpermits-rejected' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Rejected
                  </button>
                  <button
                    onClick={() => handleTabChange('workpermits-expired')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'workpermits-expired' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Expired
                  </button>
                  <button
                    onClick={() => handleTabChange('workpermits-closed')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'workpermits-closed' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Closed
                  </button>
                </div>
              )}

              {/* Nominations */}
              <button
                onClick={() => handleCategoryClick('nominations')}
                className={`flex items-center justify-between w-full px-2 py-2 text-sm font-bold rounded-xl transition-all ${
                  activeTab.startsWith('nominations') ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Award size={18} className="flex-shrink-0" />
                  <span>Nominations</span>
                </div>
                {isNominationsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {isNominationsOpen && (
                <div className="pl-6 py-1 space-y-0.5">
                  <button
                    onClick={() => handleTabChange('nominations-pending')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'nominations-pending' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => handleTabChange('nominations-approved')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'nominations-approved' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => handleTabChange('nominations-rejected')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'nominations-rejected' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Rejected
                  </button>
                  <button
                    onClick={() => handleTabChange('nominations-expired')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'nominations-expired' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Expired
                  </button>
                  <button
                    onClick={() => handleTabChange('nominations-nominees')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'nominations-nominees' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Nominee List
                  </button>
                </div>
              )}

              {/* Warning Actions */}
              <button
                onClick={() => handleCategoryClick('warnings')}
                className={`flex items-center justify-between w-full px-2 py-2 text-sm font-bold rounded-xl transition-all ${
                  activeTab.startsWith('warnings') ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle size={18} className="flex-shrink-0" />
                  <span>Warning Actions</span>
                </div>
                {isWarningsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {isWarningsOpen && (
                <div className="pl-6 py-1 space-y-0.5">
                  <button
                    onClick={() => handleTabChange('warnings-active')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'warnings-active' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => handleTabChange('warnings-expired')}
                    className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                      activeTab === 'warnings-expired' ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                    }`}
                  >
                    Expired
                  </button>
                </div>
              )}

              {/* Authorization */}
              <button
                onClick={() => handleCategoryClick('authorization')}
                className={`flex items-center justify-between w-full px-2 py-2 text-sm font-bold rounded-xl transition-all ${
                  activeTab.startsWith('authorization') ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Shield size={18} className="flex-shrink-0" />
                  <span>Authorization levels</span>
                </div>
                {isAuthorizationOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {isAuthorizationOpen && (
                <div className="pl-6 py-1 space-y-0.5">
                  {isWorkScopesLoading ? (
                    <div className="flex items-center px-2 py-1.5 text-sm text-slate-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 mr-2" />
                      <span>Loading...</span>
                    </div>
                  ) : workScopesError ? (
                    <div className="px-2 py-1.5 text-sm text-red-500">Error: {workScopesError}</div>
                  ) : workScopes.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-slate-500 italic">No authorization levels available</div>
                  ) : (
                    workScopes.map((scope) => (
                      <button
                        key={scope.id}
                        onClick={() => handleTabChange(`authorization-${scope.id}`)}
                        className={`flex items-center w-full px-2 py-1.5 rounded-lg text-sm transition-colors ${
                          activeTab === `authorization-${scope.id}` ? 'text-brand-600 font-bold bg-brand-50' : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
                        }`}
                      >
                        <span
                          className="max-h-[2.5rem] line-clamp-2 overflow-hidden text-left"
                          title={scope.name}
                        >
                          {scope.name}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Turbine Management */}
              <Link to="/ecc/turbins">
                <button
                  className={`flex items-center gap-2 w-full px-2 py-2 text-sm font-bold rounded-xl transition-all ${
                    location.pathname === '/ecc/turbins' ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <img src={icons} alt="turbine" className="h-[18px] w-[18px] flex-shrink-0" />
                  <span>Turbine Management</span>
                </button>
              </Link>
            </div>
          </nav>

          {/* Footer */}
          <div className="p-2 mt-auto border-t border-slate-100">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 w-full transition-all"
              >
                <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-black text-brand-600">
                    {user?.firstName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="text-left flex-1 min-w-0">
                  <span className="text-sm font-bold text-slate-900 block truncate">
                    {user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "Profile"}
                  </span>
                  <span className="text-[10px] font-bold text-brand-500 uppercase">ECC</span>
                </div>
              </button>
              {showUserMenu && (
                <div className="absolute left-0 bottom-full mb-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 z-50 overflow-hidden">
                  {/* User info header */}
                  <div className="p-3 border-b border-slate-100 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-black text-brand-600">
                        {user?.firstName?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900">{user?.firstName} {user?.lastName}</p>
                      <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                  {/* Menu items */}
                  <div className="p-1">
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                      onClick={() => {
                        navigate('/changes');
                        setShowUserMenu(false);
                      }}
                    >
                      <FileText size={14} />
                      Changes List
                    </button>
                    <button
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      onClick={handleLogout}
                    >
                      <LogOut size={14} />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className={`flex-1 bg-slate-50 ${activeTab.startsWith('authorization') ? '' : 'overflow-y-auto'}`}>
          {console.log('Rendering main: activeTab=', activeTab, 'location.pathname=', location.pathname)}
          {activeTab.startsWith('authorization') && (
            <>
              {console.log('Rendering WorkScopeRequirementsPage with activeSubTab=', activeTab)}
              <WorkScopeRequirementsPage
                activeSubTab={activeTab}
                onWorkScopeChange={refreshWorkScopes}
                onActiveTabChange={onActiveTabChange}
              />
            </>
          )}
          <div className="container mx-auto ">
            {activeTab.startsWith('nominations') && (
              <>
                {console.log('Rendering ECCNominationTab with activeSubTab=', activeTab)}
                <ECCNominationTab activeSubTab={activeTab} />
              </>
            )}
            {activeTab.startsWith('workpermits') && (
              <>
                {console.log('Rendering ECCWorkPermitTab with activeSubTab=', activeTab)}
                <ECCWorkPermitTab activeSubTab={activeTab} />
              </>
            )}
            {activeTab.startsWith('warnings') && (
              <>
                {console.log('Rendering ECCWarningActionsTab with activeSubTab=', activeTab)}
                <ECCWarningActionsTab activeSubTab={activeTab} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-white px-5 py-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <LogOut className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-graphite-900">Log Out</h3>
                  <p className="text-sm text-graphite-500">Confirm your action</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-graphite-700">Are you sure you want to log out of your account?</p>
            </div>
            <div className="px-5 py-4 border-t border-steel-100 flex justify-end gap-2">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 text-sm font-medium text-graphite-700 border border-steel-300 rounded-full hover:bg-steel-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 transition-colors"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ECCDashboard;